import { normalizePubNubTimetoken } from './pubnubTimetoken';

export const CHAT_LIST_HISTORY_FETCH_COUNT = 25;
// 一覧除外messageが連続して深くても、走査がPubNubの保持履歴長に比例して伸びない
// よう打ち切る上限ページ数。超過時はfailure扱いにして既存previewを保持する。
export const CHAT_LIST_HISTORY_MAX_PAGES = 10;

export type ChatListMessageRecord = {
  message: unknown;
  timetoken: string | number;
  uuid?: string;
};

export type ChatListHistoryPage<TRecord extends ChatListMessageRecord> = {
  records: readonly TRecord[];
  moreStart?: unknown;
  hasMoreMetadata?: boolean;
};

/** raw response.moreを保持し、壊れたmoreを正常な履歴末尾と区別する。 */
export function buildChatListHistoryPage<
  TRecord extends ChatListMessageRecord,
>(
  records: readonly TRecord[],
  responseMore: unknown,
): ChatListHistoryPage<TRecord> {
  const hasMoreMetadata = responseMore != null;
  const moreStart =
    responseMore &&
    typeof responseMore === 'object' &&
    !Array.isArray(responseMore)
      ? (responseMore as { start?: unknown }).start
      : undefined;

  return { records, moreStart, hasMoreMetadata };
}

/**
 * 一覧hydrate effectの依存キー。表示中channelの集合と、markerを信頼する管理
 * participant UUIDの集合が変わったときだけ変化する。unreadCountや並び順だけの
 * 一覧再取得では変わらないため、そのたびに全channelの履歴を取り直さずに済む。
 */
export function buildChatListHydrationKey(
  channels: readonly { id: string; managementParticipantUuids?: unknown }[],
): string {
  return channels
    .map((channel) => {
      const uuids = Array.isArray(channel.managementParticipantUuids)
        ? channel.managementParticipantUuids
            .filter((uuid): uuid is string => typeof uuid === 'string')
            .sort()
        : [];
      return JSON.stringify([channel.id, uuids]);
    })
    .sort()
    .join('\n');
}

type ChatListExclusionPayload = {
  excludeFromChatList?: unknown;
};

function isExcludedFromChatList(payload: unknown): boolean {
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
    return false;
  }

  return (payload as ChatListExclusionPayload).excludeFromChatList === true;
}

/**
 * 一覧除外markerは認証済み管理APIが示した管理publisherにだけ適用する。
 * 利用者や未知publisherが同じmarkerを送っても新着・未読を隠さない。
 */
export function isChatListMessageVisible(
  record: Pick<ChatListMessageRecord, 'message' | 'uuid'>,
  managementParticipantUuids: unknown,
): boolean {
  if (!isExcludedFromChatList(record.message)) return true;

  // 認証済み管理APIが返した管理参加者UUIDとpublisherが一致するときだけ
  // markerを信頼する。不正なruntime値や不明なpublisherは通常メッセージとして扱う。
  if (!record.uuid) return true;
  const trustedManagementUuids = Array.isArray(managementParticipantUuids)
    ? managementParticipantUuids
        .filter((uuid): uuid is string => typeof uuid === 'string')
        .map((uuid) => uuid.trim())
        .filter(Boolean)
    : [];
  return !trustedManagementUuids.includes(record.uuid);
}

function parsePubNubTimetoken(value: string | number): bigint | null {
  // Number.MAX_SAFE_INTEGERを超えたnumberは既に下位桁を復元できない。
  if (typeof value === 'number' && !Number.isSafeInteger(value)) return null;
  const normalized = normalizePubNubTimetoken(value);
  if (!/^\d+$/.test(normalized)) return null;

  try {
    return BigInt(normalized);
  } catch {
    return null;
  }
}

/** Numberへ丸めずPubNub timetokenを比較する。parse不能ならnull。 */
export function compareChatListMessageTimetokens(
  left: string | number,
  right: string | number,
): -1 | 0 | 1 | null {
  const leftToken = parsePubNubTimetoken(left);
  const rightToken = parsePubNubTimetoken(right);
  if (leftToken == null || rightToken == null) return null;
  if (leftToken === rightToken) return 0;
  return leftToken < rightToken ? -1 : 1;
}

/**
 * PubNub履歴の返却順には依存せず、timetokenが新しい順に一覧対象を探す。
 * 入力配列は変更しない。
 */
export function findLatestVisibleChatListMessage<
  TRecord extends ChatListMessageRecord,
>(
  records: readonly TRecord[],
  managementParticipantUuids: unknown,
): TRecord | undefined {
  return [...records]
    .filter((record) => parsePubNubTimetoken(record.timetoken) != null)
    .sort((left, right) => {
      const comparison = compareChatListMessageTimetokens(
        left.timetoken,
        right.timetoken,
      );
      return comparison == null ? 0 : -comparison;
    })
    .find((record) =>
      isChatListMessageVisible(record, managementParticipantUuids),
    );
}

/**
 * 同一channelの候補からexact timetokenが新しい方を選ぶ。
 * 同値なら履歴側のactions等を反映できるようcandidateを採用する。
 */
export function selectLatestChatListMessage<
  TRecord extends ChatListMessageRecord,
>(current: TRecord | undefined, candidate: TRecord): TRecord {
  if (!current) return candidate;

  const currentToken = parsePubNubTimetoken(current.timetoken);
  const candidateToken = parsePubNubTimetoken(candidate.timetoken);

  if (candidateToken == null) return currentToken == null ? candidate : current;
  if (currentToken == null) return candidate;
  return candidateToken >= currentToken ? candidate : current;
}

/** channelごとの候補を、既存の新しいpreviewを巻き戻さずに統合する。 */
export function mergeLatestChatListMessages<
  TRecord extends ChatListMessageRecord,
>(
  current: Readonly<Record<string, TRecord>>,
  candidates: Readonly<Record<string, TRecord>>,
): Record<string, TRecord> {
  let next = current as Record<string, TRecord>;

  for (const [channelId, candidate] of Object.entries(candidates)) {
    const selected = selectLatestChatListMessage(current[channelId], candidate);
    if (selected === current[channelId]) continue;
    if (next === current) next = { ...current };
    next[channelId] = selected;
  }

  return next;
}

/**
 * hydrateが正常に履歴末尾まで到達したchannelだけ、request開始時からpreviewが
 * 変わっていなければ空結果を反映する。失敗channelや途中realtimeは保持する。
 */
export function mergeHydratedChatListMessages<
  TRecord extends ChatListMessageRecord,
>(params: {
  current: Readonly<Record<string, TRecord>>;
  candidates: Readonly<Record<string, TRecord>>;
  emptyChannelIds: readonly string[];
  previewsAtRequestStart: Readonly<Record<string, TRecord | undefined>>;
}): Record<string, TRecord> {
  const { current, candidates, emptyChannelIds, previewsAtRequestStart } =
    params;
  let next = mergeLatestChatListMessages(current, candidates);

  for (const channelId of emptyChannelIds) {
    if (current[channelId] !== previewsAtRequestStart[channelId]) continue;
    if (!(channelId in next)) continue;
    if (next === current) next = { ...current };
    delete next[channelId];
  }

  return next;
}

/** 最新API trust setで、過去にfail-openで入った一覧除外messageも再評価する。 */
export function retainVisibleChatListMessages<
  TRecord extends ChatListMessageRecord,
>(
  current: Readonly<Record<string, TRecord>>,
  channels: readonly {
    id: string;
    managementParticipantUuids?: unknown;
  }[],
): Record<string, TRecord> {
  let next = current as Record<string, TRecord>;

  for (const channel of channels) {
    const record = current[channel.id];
    if (
      !record ||
      isChatListMessageVisible(record, channel.managementParticipantUuids)
    ) {
      continue;
    }
    if (next === current) next = { ...current };
    delete next[channel.id];
  }

  return next;
}

/**
 * 先頭ページが一覧除外メッセージだけで埋まっている間、履歴を古い方向へ辿る。
 * cursorが進まない異常レスポンスでは停止し、無限ループを避ける。
 */
export async function findLatestVisibleChatListMessageWithPagination<
  TRecord extends ChatListMessageRecord,
>(params: {
  initialPage: ChatListHistoryPage<TRecord>;
  managementParticipantUuids: unknown;
  fetchOlder: (start: string) => Promise<ChatListHistoryPage<TRecord>>;
  pageSize?: number;
  maxPages?: number;
}): Promise<TRecord | undefined> {
  let page = params.initialPage;
  const visitedCursors = new Set<string>();
  const pageSize = params.pageSize ?? CHAT_LIST_HISTORY_FETCH_COUNT;
  const maxPages = params.maxPages ?? CHAT_LIST_HISTORY_MAX_PAGES;
  let previousCursorToken: bigint | null = null;
  let pagesScanned = 0;

  while (true) {
    pagesScanned += 1;
    if (page.records.length > pageSize) {
      throw new Error('PubNub chat-list history page exceeds requested count');
    }
    let oldestPageToken: bigint | null = null;
    for (const record of page.records) {
      const token = parsePubNubTimetoken(record.timetoken);
      if (token == null) {
        throw new Error('PubNub chat-list history timetoken is unavailable');
      }
      if (previousCursorToken != null && token >= previousCursorToken) {
        throw new Error('PubNub chat-list history cursor did not advance');
      }
      if (oldestPageToken == null || token < oldestPageToken) {
        oldestPageToken = token;
      }
    }

    const visible = findLatestVisibleChatListMessage(
      page.records,
      params.managementParticipantUuids,
    );
    if (visible) return visible;

    // fetchMessagesのresponse.more.startを優先する。通常historyではmoreが
    // 省略されることもあるため、満杯のページだけ最古exact tokenへfallbackする。
    let nextStart = page.moreStart;
    if (page.hasMoreMetadata && nextStart == null) {
      throw new Error('PubNub chat-list history cursor is unavailable');
    }
    if (nextStart == null && page.records.length === pageSize) {
      let oldestToken: bigint | null = null;
      for (const record of page.records) {
        const token = parsePubNubTimetoken(record.timetoken);
        if (token == null) {
          throw new Error('PubNub chat-list history cursor is unavailable');
        }
        if (oldestToken == null || token < oldestToken) oldestToken = token;
      }
      nextStart = oldestToken?.toString();
    }
    if (nextStart == null) return undefined;
    if (typeof nextStart !== 'string' && typeof nextStart !== 'number') {
      throw new Error('PubNub chat-list history cursor is unavailable');
    }

    const cursorToken = parsePubNubTimetoken(nextStart);
    if (cursorToken == null) {
      throw new Error('PubNub chat-list history cursor is unavailable');
    }
    if (oldestPageToken != null && cursorToken > oldestPageToken) {
      throw new Error('PubNub chat-list history cursor did not advance');
    }
    if (previousCursorToken != null && cursorToken >= previousCursorToken) {
      throw new Error('PubNub chat-list history cursor did not advance');
    }

    const cursor = cursorToken.toString();
    if (visitedCursors.has(cursor)) {
      throw new Error('PubNub chat-list history cursor did not advance');
    }
    // 上限ページ数まで確認しても確定しない場合はfailureにし、呼び出し側は
    // 空結果として扱わず既存previewを保持する（backendの走査上限と同じ方針）。
    if (pagesScanned >= maxPages) {
      throw new Error('PubNub chat-list history scan exceeded max pages');
    }
    visitedCursors.add(cursor);
    previousCursorToken = cursorToken;

    page = await params.fetchOlder(cursor);
  }
}
